import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
  danger: '#F87171',
};

interface Row {
  slug: string;
  url: string;
  kind: 'identify' | 'diagnose';
  name: string;
  sharer: string;
  guest: boolean;
  country: string;
  view_count: number;
  image_url: string;
  card_url: string;
  created_at: string;
  revoked_at?: string | null;
}
interface Resp {
  total: number;
  active: number;
  revoked: number;
  views: number;
  rows: Row[];
}

function when(iso: string): string {
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 60) return `${Math.max(1, Math.round(mins))} min ago`;
  const h = mins / 60;
  if (h < 24) return `${Math.round(h)}h ago`;
  const d = h / 24;
  if (d < 30) return `${Math.round(d)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function SharesTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/shares`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setD((j.data ?? j) as Resp);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function revoke(row: Row) {
    // Revoking kills a link that may already be posted publicly, so make the
    // consequence explicit rather than relying on an undo that does not exist.
    const ok = window.confirm(
      `Stop sharing "${row.name}"?\n\n` +
      `${row.url}\n\n` +
      `Anyone who opens this link will see "no longer available". ` +
      `The link cannot be brought back.`
    );
    if (!ok) return;
    setBusy(row.slug);
    try {
      const res = await fetch(`${API_BASE}/admin/shares/${row.slug}`, {
        method: 'DELETE', headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to revoke');
    } finally {
      setBusy(null);
    }
  }

  const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px',
  };
  const td: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  };
  const th: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };

  if (error) return <div style={{ color: C.danger, fontSize: 13.5, marginTop: 20 }}>{error}</div>;
  if (!d) return null;

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: C.text }}>Shared plants</h2>
        <span style={{ marginLeft: 'auto', color: C.sub, fontSize: 12 }}>
          public pages under gardenoai.com/s/
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: 12, marginBottom: 14,
      }}>
        {[
          { label: 'Total shares', value: d.total },
          { label: 'Live now', value: d.active, color: d.active > 0 ? '#34D399' : undefined },
          { label: 'Revoked', value: d.revoked },
          { label: 'Views', value: d.views },
        ].map((k) => (
          <div key={k.label} style={card}>
            <div style={{
              color: C.sub, fontSize: 11.5, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: k.color || C.text }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.6 }}>
        Every share page carries a Report link. Revoking sets the page to "no longer
        available" and burns the slug so it can never be reissued — but the card image
        itself stays on the public R2 bucket, so anyone who already saved that direct
        URL keeps it. Views exclude link-preview crawlers.
      </p>

      <div style={{
        background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 14, overflowX: 'auto',
      }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
          <thead>
            <tr>
              <th style={th}>Plant</th>
              <th style={th}>Shared by</th>
              <th style={th}>Kind</th>
              <th style={{ ...th, textAlign: 'right' }}>Views</th>
              <th style={{ ...th, textAlign: 'right' }}>When</th>
              <th style={{ ...th, textAlign: 'right' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const dead = !!r.revoked_at;
              return (
                <tr key={r.slug} style={{ background: dead ? 'transparent' : C.panelAlt, opacity: dead ? 0.55 : 1 }}>
                  <td style={td}>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: C.text, fontWeight: 600, textDecoration: 'none' }}
                    >
                      {r.name}
                    </a>
                    <div style={{ color: C.sub, fontSize: 11.5, marginTop: 2 }}>/s/{r.slug}</div>
                  </td>
                  <td style={{ ...td, fontSize: 12.5 }}>
                    {r.sharer}
                    {r.guest && (
                      <span style={{ marginLeft: 7, fontSize: 10.5, color: C.sub, fontWeight: 700 }}>
                        GUEST
                      </span>
                    )}
                    {r.country && (
                      <div style={{ color: C.sub, fontSize: 11.5, marginTop: 2 }}>{r.country}</div>
                    )}
                  </td>
                  <td style={{ ...td, color: C.sub, fontSize: 12.5 }}>
                    {r.kind === 'diagnose' ? 'Diagnosis' : 'Identification'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {r.view_count}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: C.sub, fontSize: 12.5 }}>
                    {when(r.created_at)}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {dead ? (
                      <span style={{ color: C.sub, fontSize: 12 }}>Revoked</span>
                    ) : (
                      <button
                        onClick={() => revoke(r)}
                        disabled={busy === r.slug}
                        style={{
                          background: 'transparent', color: C.danger,
                          border: `1px solid ${C.border}`, borderRadius: 8,
                          padding: '5px 11px', fontSize: 12, fontWeight: 700,
                          cursor: busy === r.slug ? 'default' : 'pointer',
                          opacity: busy === r.slug ? 0.5 : 1,
                        }}
                      >
                        {busy === r.slug ? 'Stopping…' : 'Stop sharing'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {d.rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...td, textAlign: 'center', color: C.sub }}>
                  Nobody has shared a plant yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SharesTab;
